/* =========================================================================
 * RESOLVE — the three answers the hunt cannot give on its own.
 *
 * Brian: "if there is contradicting information there can be a flag, but if it
 * says there are multiple winners, there needs to be a new winner created. Also
 * if we have to manually type the winner in because that's the only source then
 * we should be able to manually input it if completely necessary."
 *
 *   CONTRADICTION → flagged, never overwritten. The server refuses a crown that
 *                   disagrees with a champion already on the record and writes a
 *                   `champion_conflict` ledger entry. This surfaces BOTH names
 *                   so a person can pick, instead of showing a red string.
 *   MULTIPLE       → a SPLIT. Each division becomes its own championship record.
 *   MANUAL         → a person types what they know, with a BASIS. No source URL
 *                   required: a rep who was at the game has better information
 *                   than any page. But a manual champion with no stated basis is
 *                   a rumour with a name on it, so the basis is mandatory.
 *
 * All three were already built on the backend and had no way in from this app.
 *
 * A MANUAL ENTRY LOCKS THE RECORD. That is the point of a human write layer —
 * if automation could overwrite it tomorrow the person's work was decoration.
 * The lock is stated on screen rather than being a surprise afterwards.
 * ========================================================================= */

import { useState } from 'react'
import { api } from '../../../lib/api.js'
import {
  checkResolution, readResolveError, OUTCOME_META,
  houseRules, houseWarnings, recordDivisions, repRunId, withActor,
  decidedState, recordEventDate,
} from './resolveModel.js'
import { planSplit, SAME_EVENT_DAYS } from './splitPlan.js'
import { useAccess } from '../../../lib/access.jsx'
import { ACTIONS } from '../../../lib/rbac.js'
import styles from './pursuit.module.css'

/**
 * WHAT THE MACHINE ALREADY DID, STATED BEFORE THE REP TYPES ANYTHING.
 *
 * The three states a rep needs told apart, and they are genuinely different
 * work rather than three shades of "no champion":
 *
 *   NEVER ASKED      — nobody has queued it. Ask first; the sweep may answer.
 *   QUEUED, WAITING  — asked, the sweep has not reached it. ⚠️ The hunt is
 *                      SILENT today, so this can sit for a long time, and
 *                      saying "waiting" without saying that would quietly
 *                      advise a rep to keep waiting on an engine that is down.
 *   SEARCHED, EMPTY  — it has been looked for, n times, and not found. This is
 *                      the state Brian's requirement exists for.
 *
 * ⚠️ IT REPORTS, IT NEVER BLOCKS. A `0` attempt count is not rendered as a
 * bare zero; a never-searched record says so in words.
 */
function HuntState({ record }) {
  const tag = String(record?.championSearchTag || '').trim().toLowerCase()
  const attempts = Number(record?.championSearchAttempts || 0)
  const searchedAt = String(record?.championSearchedAt || '').trim()

  let head = 'The system has not been asked to find this one yet'
  let body = 'Ask it first — it may already be findable. You can still enter the champion '
    + 'yourself below if you know it.'
  if (attempts > 0) {
    head = attempts === 1
      ? 'The system searched and came back empty'
      : `The system searched ${attempts} times and came back empty`
    body = 'That is what this is for. Find it yourself and enter it below — '
      + 'what you record outranks the machine permanently.'
  } else if (tag === 'requested') {
    head = 'Queued for the system, not searched yet'
    /* ⚠️ NAMING THE OUTAGE IS THE HONEST PART. "Waiting" on its own would
     * advise a rep to keep waiting on an engine that is not running. */
    body = 'The sweep has not reached it. If you already know who won, do not wait — '
      + 'enter it below.'
  }
  if (searchedAt) body += ` Last searched ${searchedAt.slice(0, 10)}.`

  return (
    <div className={styles.resolveHunt}>
      <b>{head}</b>
      <span>{body}</span>
    </div>
  )
}

export default function ResolvePanel({ record, onResolved, onClose, conflict = null, seedDivisions = null, seedSource = '', startMode = '' }) {
  /* ⚠️ RESOURCE-SCOPED, not a capability check.
   *
   * POST /api/champion-resolve requires CC_CHAMPION_UPDATE, which is OWN for a
   * rep — they may resolve records in their own book and no others. So this is
   * can(action, RECORD), the authorization question, not can(action), the
   * "could they ever" question. ops and marketing are denied outright.
   *
   * This panel shipped with no permission check at all, which drew a full
   * manual-entry and split form for people the server would refuse. */
  /* `ctx.user` is the signed-in actor. It is read for the AUDIT TRAIL only —
   * the server derives identity from the verified JWT and that is the one that
   * counts. This makes the trail readable without joining to a token. */
  const { can, why, ctx } = useAccess()
  const actor = ctx?.user || null
  const mayResolve = can(ACTIONS.CC_CHAMPION_UPDATE, record)
  const id = String(record?.id || '')
  /* A conflict arrives from a refused Record, so the panel opens straight into
   * the answer to it rather than making somebody find the tab. */
  /* Seeded divisions mean the hunt already found several winners for this one
   * record, so the split is the answer and the form opens on it. Making
   * somebody retype what the hunt just returned is the work this exists to
   * remove. */
  /* ⚠️ `startMode` OPENS THE SPLIT WITHOUT FABRICATING DIVISIONS.
   *
   * Brian: "For multiple champions at an event, there should be record of all
   * of them." A row that says "crowns 10 weight classes" and offers no way to
   * record them is the doorless-button pattern again, one level up.
   *
   * Seeding two blank divisions would have opened split mode too — and would
   * have invented structure the source never stated. This opens the form and
   * lets the operator name the real divisions. */
  const [mode, setMode] = useState(
    conflict ? 'manual' : (seedDivisions?.length >= 2 ? 'split' : (startMode || ''))
  )
  const [champion, setChampion] = useState(conflict?.proposed || '')
  const [basis, setBasis] = useState('')
  const [reason, setReason] = useState('')
  const [sourceUrl, setSourceUrl] = useState(seedSource || '')
  const [divisions, setDivisions] = useState(
    seedDivisions?.length >= 2
      ? seedDivisions.map((d) => ({ division: d.division || '', champion: d.champion || '' }))
      : [{ division: '', champion: '', date: '' }, { division: '', champion: '', date: '' }]
  )
  /* 🔴 THE YEAR AND THE DIVISION — the two fields the five-part crown was
   * missing. Brian: *"THE DATE INCLUDES THE YEAR — it is identity, not
   * metadata"* and *"the wrong division is worse than a blank."* Neither was
   * collected by this form and neither is demanded by the server, so a rep
   * entry could land naming a champion with no season and no division on an
   * event that crowns 226 of them. */
  const [eventDate, setEventDate] = useState('')
  const [division, setDivision] = useState('')
  /* ⭐ THE EARLY-CLINCH OVERRIDE. A standings title can be settled before its
   * final event — SoCon football in week 8, Chevrolet at Petit Le Mans — so a
   * future-dated record CAN take a champion. It is a deliberate second step:
   * the refusal is read first, then the rep asserts the clinch and links where
   * it was declared. */
  const [earlyClinch, setEarlyClinch] = useState(false)
  const [clinchSourceUrl, setClinchSourceUrl] = useState('')
  const [state, setState] = useState({ status: 'idle' })
  const divisionOptions = recordDivisions(record)

  if (!id) return null
  if (!mayResolve) {
    return (
      <div className={styles.resolveDenied}>
        {why(ACTIONS.CC_CHAMPION_UPDATE, record) || 'Recording a champion is not available for your role.'}
      </div>
    )
  }

  const payload = () => {
    /* 🔴 ONE REFUSABLE WRITE, CARRYING ALL OF IT. The crown transaction is
     * champion + source + runId + actor + timestamp, and 63 sourceless crowns
     * were once minted by a path that wrote a subset. `runId` is in this
     * endpoint's contract and this client had never sent one, so every resolve
     * ever written from this app landed with no run to trace it to. */
    const base = {
      id,
      reason: withActor(reason.trim(), actor),
      sourceUrl: sourceUrl.trim() || undefined,
      runId: repRunId(actor),
    }
    if (mode === 'manual') {
      return {
        ...base,
        outcome: 'manual',
        champion: champion.trim(),
        basis: basis.trim(),
        eventDate: eventDate.trim() || undefined,
        division: division.trim() || undefined,
        /* 🔴 AN EARLY CLINCH IS RECORDED, NOT JUST PERMITTED. A crown written
         * against a future event date must stay distinguishable from one
         * written after the event — forever. It rides in the basis so it
         * survives on the record itself rather than only in a client flag. */
        ...(earlyClinch ? {
          earlyClinch: true,
          clinchSourceUrl: clinchSourceUrl.trim(),
          basis: `${basis.trim()} [EARLY CLINCH — title decided before the event date; `
            + `declared at ${clinchSourceUrl.trim()}]`,
        } : {}),
      }
    }
    if (mode === 'split') return { ...base, outcome: 'split', divisions: divisions.filter((d) => d.division.trim()) }
    return { ...base, outcome: 'not_found' }
  }

  /* ⚠️ TWO LAYERS, AND THEY ARE NOT THE SAME LAYER.
   * `checkResolution` mirrors the SERVER, so a refusal a rep meets on submit
   * is one this form already showed them in the same words. `houseRules` is
   * SR's own, stricter bar — the year, the division, and a source that says
   * HOW rather than THAT. The server would accept a write that fails those;
   * SR will not put its name on one. Keeping them separate is what stops the
   * mirror becoming a lie. */
  const problems = mode
    ? [...checkResolution(payload()), ...houseRules(payload(), record)]
    : []
  /* ⚠️ WARNINGS ARE NOT PROBLEMS AND MUST NOT BLOCK. The season field is wrong
   * on real records — the PLL title crowned in the live test carries SEASON
   * 2026 against an event date of 2025-09-14 — so refusing on it would block
   * correct writes on every disputed record. Named, not enforced. */
  const warnings = mode ? houseWarnings(payload(), record) : []
  const ready = mode && problems.length === 0
  const notDecided = decidedState(record) === 'future'

  const submit = async (dryRun = false) => {
    setState({ status: 'saving' })
    try {
      const r = await api.championResolve({ ...payload(), ...(dryRun ? { dryRun: true } : {}) })
      if (dryRun) { setState({ status: 'plan', plan: r.plan || [] }); return }
      setState({ status: 'done', result: r })
      /* Repaint the row. A manual champion the board still shows as missing is
       * the two-sources-of-truth fault all over again. */
      if (mode === 'manual') onResolved && onResolved(id, { champion: champion.trim() })
      else onResolved && onResolved(id, {})
    } catch (err) {
      setState({ status: 'refused', refusal: readResolveError(err) })
    }
  }

  return (
    <div className={styles.resolve} onClick={(e) => e.stopPropagation()}>
      {/* THE CONTRADICTION FLAG. Both names, side by side, with the record's
          own value first — because the one already written is the one somebody
          previously decided was right. */}
      {conflict && (
        <div className={styles.resolveConflict}>
          {/* TWO SHAPES OF THE SAME REFUSAL, and they must not be flattened.
              A resolve that clashes has a second NAME to weigh against the
              first. A queue request refused on an already-crowned record has
              only the name that is there — printing an empty "the hunt found:"
              beside it would invent a disagreement that nobody reported. */}
          <div className={styles.resolveConflictHead}>
            {conflict.proposed ? 'Two sources disagree' : 'This one already names a champion'}
          </div>
          <div className={styles.resolveVs}>
            <span><b>On the record:</b> {conflict.existing}</span>
            {conflict.proposed && <span><b>The hunt found:</b> {conflict.proposed}</span>}
          </div>
          <p className={styles.resolveConflictWhy}>
            {conflict.proposed
              ? 'Nothing was overwritten and the disagreement is logged. Decide which is right — recording the new one below replaces it and locks the record.'
              : 'Nothing was changed. If that champion is wrong, correct it below — recording replaces it and locks the record.'}
          </p>
        </div>
      )}

      {/* 🔴 THE SYSTEM TRIES FIRST, AND THE REP SEES THAT IT TRIED.
        *
        * Brian: *"Ask the system → it runs → it comes back with a champion, or
        * empty with its reason → THEN the manual path opens. Do not offer
        * manual entry as the first move."*
        *
        * ⚠️ BUT IT STATES, IT DOES NOT BLOCK. *"If the hunt is down or queued,
        * say so plainly and still let the rep proceed — a dead engine must not
        * block a sale."* `sr-champion-hunt` was reported silent and 14,742
        * records uncrowned (reported to this lane 2026-10-04, not measured
        * here); a gate that waited on the sweep would make the
        * engine being down into a reason a rep cannot close a deal. So this
        * reports the machine's state and leaves the manual path reachable
        * underneath it — the ordering is in what leads, not in what is locked.
        */}
      {!mode && (
        <>
          <HuntState record={record} />
          <div className={styles.resolveModes}>
            {['manual', 'split', 'not_found'].map((k) => (
              <button key={k} type="button" className={styles.resolveMode} onClick={() => setMode(k)}>
                <b>{OUTCOME_META[k].label}</b>
                <span>{OUTCOME_META[k].blurb}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {mode && state.status !== 'done' && (
        <div className={styles.resolveForm}>
          <div className={styles.resolveFormHead}>
            <b>{OUTCOME_META[mode].label}</b>
            <button type="button" className={styles.resolveBack} onClick={() => { setMode(''); setState({ status: 'idle' }) }}>
              change
            </button>
          </div>

          {mode === 'manual' && (
            <>
              <label className={styles.resolveField}>
                <span>Champion</span>
                <input value={champion} onChange={(e) => setChampion(e.target.value)} placeholder="Full school or organisation name" />
              </label>
              <label className={styles.resolveField}>
                <span>How do you know? *</span>
                <input value={basis} onChange={(e) => setBasis(e.target.value)}
                  placeholder="I was at the game · the coach told me · the school posted it · read the bracket" />
                {/* ⚠️ "I know who won" IS NOT A SOURCE, and it clears the
                  * server's 10-character floor. This is the field that makes
                  * the record defensible to a customer, so the check is on
                  * whether the sentence names a CHANNEL, not on its length. */}
                <small className={styles.resolveHint}>
                  Say how you know, not that you know — this is what makes the name defensible
                  to a customer.
                </small>
              </label>
              <label className={styles.resolveField}>
                <span>Source link — paste it if you have one</span>
                <input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)}
                  inputMode="url" placeholder="https://…" />
              </label>

              {/* 🔴 THE YEAR IS IDENTITY. The same championship is run every
                * season, so a champion with no season attached cannot be told
                * from the one before it — and the single most dangerous miss
                * in this whole system is a repeat champion lifted from last
                * year's recap, where the name checks out and every sanity
                * check passes while the score, opponent and venue are all
                * silently from the wrong year. */}
              <label className={styles.resolveField}>
                <span>Date it was decided *</span>
                <input type="date" value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)} />
                <small className={styles.resolveHint}>
                  The year is what tells this title from last season’s.
                </small>
              </label>

              {/* 🔴 THE DIVISION, PICKED AND NEVER INFERRED. One cheer event
                * carries 226 divisions and IMSA crowned three different 2026
                * winners across manufacturer, drivers and teams. The wrong
                * division is worse than a blank, so this is a pick-list built
                * from what the RECORD declares — a rep cannot type a division
                * this event does not have, and cannot invent one on an event
                * that has none, because the field is not rendered there. */}
              {divisionOptions.length > 0 && (
                <label className={styles.resolveField}>
                  <span>Which division? *</span>
                  <select value={division} onChange={(e) => setDivision(e.target.value)}>
                    <option value="">Pick the division…</option>
                    {divisionOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <small className={styles.resolveHint}>
                    This event crowns {divisionOptions.length}. The wrong one is worse than a blank.
                  </small>
                </label>
              )}

              {/* 🔴 THE EVENT HAS NOT HAPPENED. The refusal is read FIRST and
                * names both dates; the override is a second deliberate act.
                * Brian: "There should be no future records recorded unless
                * it's been decided already." */}
              {notDecided && (
                <div className={styles.clinchBox}>
                  <p className={styles.clinchHead}>
                    This championship has not happened yet — the record is dated{' '}
                    <strong>{recordEventDate(record)?.toLocaleDateString('en-US',
                      { year: 'numeric', month: 'short', day: 'numeric' })}</strong>.
                  </p>
                  <p className={styles.resolveNote}>
                    Check you have the right record before going further: the same championship
                    runs every season and they look identical apart from the year.
                  </p>
                  {/* ⭐ The one exception, and it is a real one. A standings
                    * title can be settled before its final event. */}
                  <label className={styles.clinchTick}>
                    <input type="checkbox" checked={earlyClinch}
                      onChange={(e) => setEarlyClinch(e.target.checked)} />
                    <span>
                      This is a <strong>standings title that has already been clinched</strong> —
                      mathematically settled before the final event, like a conference football
                      title decided in week 8.
                    </span>
                  </label>
                  {earlyClinch && (
                    <label className={styles.resolveField}>
                      <span>Where it was declared *</span>
                      <input value={clinchSourceUrl} inputMode="url" placeholder="https://…"
                        onChange={(e) => setClinchSourceUrl(e.target.value)} />
                      <small className={styles.resolveHint}>
                        The governing body’s standings or its own announcement. Prefer the body’s
                        declaration over anyone’s arithmetic.
                      </small>
                    </label>
                  )}
                </div>
              )}

              {/* ⚠️ NAMED, NOT ENFORCED. */}
              {warnings.map((w, i) => (
                <p key={i} className={styles.resolveWarn}>{w}</p>
              ))}

              <p className={styles.resolveNote}>
                Recording this names <strong>{actor?.name || 'you'}</strong> as the person who entered
                it and <strong>locks the record</strong>. No automated hunt can overwrite it — if a
                later machine run disagrees it raises a conflict for a human instead of winning.
                {earlyClinch && ' It is recorded as an EARLY CLINCH, so it stays distinguishable '
                  + 'from a champion recorded after the event.'}
              </p>
            </>
          )}

          {mode === 'split' && (
            <>
              {/* ⚠️ THE SHAPE IS SHOWN BEFORE IT IS CHOSEN, AND IT IS NOT A
                * CHOICE. Brian: "if the timing is right — parent folder with
                * children. But if it's like one now, one in the spring, they
                * are split." The DATES decide, so the operator's job is to
                * enter what the source said and read back what that means. */}
              <p className={styles.resolveNote}>
                Enter the day each title was decided. A festival whose divisions land within{' '}
                {SAME_EVENT_DAYS} days stays one event with these as its divisions; titles
                further apart were never one event and become championships in their own
                right. Leave a champion blank where the source does not name one — the
                record is still created.
              </p>
              {divisions.map((d, i) => (
                <div key={i} className={styles.resolveDiv}>
                  <input value={d.division} placeholder={`Division ${i + 1} (e.g. 4A)`}
                    onChange={(e) => setDivisions((v) => v.map((x, j) => j === i ? { ...x, division: e.target.value } : x))} />
                  <input value={d.champion} placeholder="Champion (optional)"
                    onChange={(e) => setDivisions((v) => v.map((x, j) => j === i ? { ...x, champion: e.target.value } : x))} />
                  {/* The day THIS title was decided. Blank inherits the record's
                    * date and is marked inherited downstream — a seven-day
                    * festival must not become N records dated its opening day. */}
                  <input type="date" value={d.date || ''} title="The day this title was decided — blank inherits the event date"
                    onChange={(e) => setDivisions((v) => v.map((x, j) => j === i ? { ...x, date: e.target.value } : x))} />
                </div>
              ))}
              <button type="button" className={styles.resolveAdd}
                onClick={() => setDivisions((v) => [...v, { division: '', champion: '', date: '' }])}>
                + another division
              </button>
              <label className={styles.resolveField}>
                <span>Source naming the divisions *</span>
                <input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://…" />
              </label>

              {/* ⚠️ THE SHAPE, READ BACK WHILE TYPING.
                *
                * `planSplit` is pure, so the consequence of the dates just
                * entered can be shown before anything is submitted rather than
                * discovered in the result. It matters because the two outcomes
                * are genuinely different things — one leaves a container, the
                * other renames this record into a championship — and finding
                * that out afterwards is how somebody creates thirteen records
                * they did not mean to. */}
              {(() => {
                const plan = planSplit({ record, parent: record, divisions, sourceUrl })
                const named = divisions.filter((d) => d.division.trim()).length
                if (named < 2) return null
                return (
                  <div className={styles.resolveShape} data-mode={plan.mode}>
                    {plan.mode === 'folder' ? (
                      <>
                        <b>One event, {plan.children.length} titles.</b> These land within{' '}
                        {plan.spanDays} day{plan.spanDays === 1 ? '' : 's'}, so this record stays
                        the event and each division becomes its own record beneath it. The event
                        itself holds no champion and stops being counted as one still needing a
                        winner.
                      </>
                    ) : (
                      <>
                        <b>These were never one event.</b> {plan.spanDays} days apart, so they
                        become separate championships. This record turns into{' '}
                        <b>{plan.parent.becomes || 'the earliest division'}</b> and takes its date;
                        the other {plan.toCreate.length} stand beside it as their own records.
                      </>
                    )}
                    {plan.children.some((c) => c.dateInherited) && (
                      <div className={styles.resolveShapeNote}>
                        {plan.children.filter((c) => c.dateInherited).length} division
                        {plan.children.filter((c) => c.dateInherited).length === 1 ? '' : 's'} with no
                        date entered will inherit this record’s, and be marked as inherited rather
                        than stated.
                      </div>
                    )}
                  </div>
                )
              })()}
            </>
          )}

          <label className={styles.resolveField}>
            <span>Why — this is the audit trail *</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="At least 15 characters. The next person reads this." />
          </label>

          {/* The server's own rules, shown while typing rather than after
              submitting. Same wording, so a server refusal is recognisable. */}
          {problems.length > 0 && reason.length > 0 && (
            <ul className={styles.resolveProblems}>
              {problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}

          <div className={styles.resolveActions}>
            {mode === 'split' && (
              <button type="button" className={styles.resolveDry} disabled={!ready || state.status === 'saving'}
                onClick={() => submit(true)}>
                Preview what this creates
              </button>
            )}
            <button type="button" className={styles.resolveGo} disabled={!ready || state.status === 'saving'}
              onClick={() => submit(false)}>
              {state.status === 'saving' ? 'Saving…'
                : mode === 'manual' ? 'Record this champion'
                : mode === 'split' ? 'Create the division records'
                : 'Tag as searched, not found'}
            </button>
          </div>

          {/* THE PLAN. A split creates records; showing exactly which, before
              it happens, is the difference between a decision and a leap. */}
          {state.status === 'plan' && (
            <div className={styles.resolvePlan}>
              <b>This will:</b>
              <ul>
                {state.plan.map((p, i) => (
                  <li key={i}>
                    <b>{p.division}</b> — {p.action}
                    {p.champion ? ` · champion ${p.champion}` : ' · no champion yet'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {state.status === 'refused' && <Refusal r={state.refusal} />}
        </div>
      )}

      {/* ⚠️ THE ANSWER TO A WRITE IS RENDERED, AND IT NAMES WHAT WAS WRITTEN.
        *
        * This used to say "Recorded and locked." and was then destroyed by the
        * parent in the same tick, so in practice a rep saw NOTHING. Even when
        * it did render, "Recorded and locked" does not tell somebody which
        * champion went onto which record — and after a form with five fields
        * that is exactly what they want confirmed. */}
      {state.status === 'done' && (
        <div className={styles.resolveDone}>
          <div className={styles.resolveDoneHead}>
            ✓ {mode === 'split'
              ? `Created ${state.result?.results?.length || 'the'} division records.`
              : mode === 'manual'
                ? `${champion.trim()} is recorded as the champion.`
                : 'Tagged — it will show as searched, not found.'}
          </div>
          {mode === 'manual' && (
            <p className={styles.resolveDoneWhy}>
              Locked and attributed to {actor?.name || 'you'}, so no automated hunt can
              overwrite it.{earlyClinch && ' Recorded as an EARLY CLINCH.'}
            </p>
          )}
          <button type="button" className={styles.resolveDoneBtn} onClick={() => onClose?.()}>
            Done
          </button>
        </div>
      )}
    </div>
  )
}

/* A refusal in the server's own words. `invalid_resolution` comes back as a
 * LIST of specific problems; collapsing them to "invalid" would throw away the
 * only part anybody can act on. */
function Refusal({ r }) {
  if (!r) return null
  if (r.kind === 'invalid') {
    return <ul className={styles.resolveProblems}>{r.problems.map((p) => <li key={p}>{p}</li>)}</ul>
  }
  return <div className={styles.resolveRefused}>{r.message}</div>
}
